import React from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
	Divider,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Chip,
} from "@material-ui/core";
import {
	CheckCircle,
	Error as ErrorIcon,
	Warning as WarningIcon,
	Info as InfoIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		minWidth: "600px",
		maxWidth: "800px",
	},
	successSection: {
		backgroundColor: theme.palette.success.light + "20",
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
	},
	warningSection: {
		backgroundColor: theme.palette.warning.light + "20",
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
	},
	errorSection: {
		backgroundColor: theme.palette.error.light + "20",
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
	},
	infoSection: {
		backgroundColor: theme.palette.info.light + "20",
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
	},
	summaryBox: {
		display: "flex",
		justifyContent: "space-around",
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	summaryItem: {
		textAlign: "center",
	},
	listItem: {
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
	},
	chipSuccess: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.success.contrastText,
	},
	chipError: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.error.contrastText,
	},
	chipWarning: {
		backgroundColor: theme.palette.warning.main,
		color: theme.palette.warning.contrastText,
	},
}));

export const AllocationResultDialog = ({ open, onClose, result, translate }) => {
	const classes = useStyles();

	if (!result) return null;

	const {
		allocated = [],
		failed_pallets = [],
		warnings = [],
		summary = {},
	} = result;

	const hasAllocations = allocated.length > 0;
	const hasFailures = failed_pallets.length > 0;
	const hasWarnings = warnings.length > 0;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			classes={{ paper: classes.dialogPaper }}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Typography variant="h6">
						{translate("tab.pallets.allocation_result_title")}
					</Typography>
					<Box>
						{summary.allocated_count > 0 && (
							<Chip
								label={`${summary.allocated_count} ${translate("tab.pallets.allocated")}`}
								className={classes.chipSuccess}
								size="small"
								style={{ marginRight: 8 }}
							/>
						)}
						{summary.failed_pallets_count > 0 && (
							<Chip
								label={`${summary.failed_pallets_count} ${translate("tab.pallets.failed")}`}
								className={classes.chipError}
								size="small"
							/>
						)}
					</Box>
				</Box>
			</DialogTitle>

			<DialogContent>
				{/* Summary Section */}
				<Box className={classes.infoSection}>
					<Typography variant="subtitle1" gutterBottom>
						<InfoIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
						{translate("tab.pallets.summary")}
					</Typography>
					<Box className={classes.summaryBox}>
						<Box className={classes.summaryItem}>
							<Typography variant="h4">{summary.total_pallets || 0}</Typography>
							<Typography variant="caption">
								{translate("tab.pallets.total_pallets")}
							</Typography>
						</Box>
						<Box className={classes.summaryItem}>
							<Typography variant="h4" color="primary">
								{summary.allocated_count || 0}
							</Typography>
							<Typography variant="caption">
								{translate("tab.pallets.allocated")}
							</Typography>
						</Box>
						<Box className={classes.summaryItem}>
							<Typography variant="h4" color="error">
								{summary.failed_pallets_count || 0}
							</Typography>
							<Typography variant="caption">
								{translate("tab.pallets.failed")}
							</Typography>
						</Box>
					</Box>

					{/* Storage Place Types Used */}
					{hasAllocations && (
						<Box mt={2}>
							<Typography variant="body2">
								<strong>{translate("tab.pallets.storage_places_used")}:</strong>
							</Typography>
							<List dense>
								{summary.customer_product_places_used > 0 && (
									<ListItem className={classes.listItem}>
										<ListItemText
											primary={`${translate("tab.pallets.customer_product_places")}: ${summary.customer_product_places_used}`}
										/>
									</ListItem>
								)}
								{summary.customer_only_places_used > 0 && (
									<ListItem className={classes.listItem}>
										<ListItemText
											primary={`${translate("tab.pallets.customer_only_places")}: ${summary.customer_only_places_used}`}
										/>
									</ListItem>
								)}
								{summary.other_places_used > 0 && (
									<ListItem className={classes.listItem}>
										<ListItemText
											primary={`${translate("tab.pallets.other_places")}: ${summary.other_places_used}`}
										/>
									</ListItem>
								)}
							</List>
						</Box>
					)}
				</Box>

				{/* Successfully Allocated Pallets */}
				{hasAllocations && (
					<Box className={classes.successSection}>
						<Typography variant="subtitle1" gutterBottom>
							<CheckCircle style={{ verticalAlign: "middle", marginRight: 8 }} />
							{translate("tab.pallets.successfully_allocated")} ({allocated.length})
						</Typography>
						<List dense>
							{allocated.map((item, index) => (
								<ListItem key={index} className={classes.listItem}>
									<ListItemIcon>
										<CheckCircle color="primary" fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={
											<>
												<strong>{translate("tab.pallets.pallet_id")}:</strong> {item.pallet_id}
												{" → "}
												<strong>{translate("tab.pallets.storage_place")}:</strong>{" "}
												{item.storage_place_address || item.storage_place_id}
											</>
										}
									/>
								</ListItem>
							))}
						</List>
					</Box>
				)}

				{/* Failed Pallets */}
				{hasFailures && (
					<Box className={classes.errorSection}>
						<Typography variant="subtitle1" gutterBottom>
							<ErrorIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
							{translate("tab.pallets.failed_allocations")} ({failed_pallets.length})
						</Typography>
						<List dense>
							{failed_pallets.map((item, index) => (
								<ListItem key={index} className={classes.listItem}>
									<ListItemIcon>
										<ErrorIcon color="error" fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={
											<>
												<strong>{translate("tab.pallets.pallet_id")}:</strong> {item.pallet_id}
											</>
										}
										secondary={
											<Typography variant="body2" color="error">
												{translate("tab.pallets.reason")}: {item.reason}
											</Typography>
										}
									/>
								</ListItem>
							))}
						</List>
					</Box>
				)}

				{/* Warnings */}
				{hasWarnings && (
					<Box className={classes.warningSection}>
						<Typography variant="subtitle1" gutterBottom>
							<WarningIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
							{translate("tab.pallets.warnings")}
						</Typography>
						<List dense>
							{warnings.map((warning, index) => (
								<ListItem key={index} className={classes.listItem}>
									<ListItemIcon>
										<WarningIcon color="action" fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={
											warning === "partial_allocation"
												? translate("tab.pallets.warning_partial_allocation")
												: warning === "insufficient_places"
												? translate("tab.pallets.warning_insufficient_places", {
														unallocated: summary.unallocated_count,
												  })
												: warning === "no_free_places"
												? translate("tab.pallets.warning_no_free_places")
												: warning
										}
									/>
								</ListItem>
							))}
						</List>
					</Box>
				)}

				{/* Rejection Reasons (if no free places) */}
				{summary.rejection_reasons && summary.rejection_reasons.length > 0 && (
					<Box className={classes.warningSection} mt={2}>
						<Typography variant="body2">
							<strong>{translate("tab.pallets.rejection_reasons")}:</strong>
						</Typography>
						<List dense>
							{summary.rejection_reasons.map((reason, index) => (
								<ListItem key={index} className={classes.listItem}>
									<ListItemText primary={`• ${reason}`} />
								</ListItem>
							))}
						</List>
					</Box>
				)}
			</DialogContent>

			<Divider />

			<DialogActions>
				<Button onClick={onClose} color="primary" variant="contained">
					{translate("ra.action.close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
